import {
  QuestionCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { Button } from "syngenta-digital-cropwise-react-ui-kit";
import { type TrialDataInterface } from "./types";
import { Popconfirm, Switch, Dropdown, Tooltip, type MenuProps } from "antd";
import { useTranslation } from "react-i18next";
import {
  TrialCreationAmplitudeEvent,
  TrialReportingAmplitudeEvent,
} from "../../shared/utils/amplitudeEvents";
import { formatTranslation } from "../../shared/utils/translationUtils";
import useAxios from "../../shared/custom-hooks/useAxios";
import { ReactComponent as MenuIcon } from "../../assets/images/Menu.svg";
import { ReactComponent as DownloadIcon } from "../../assets/images/archive.svg";
import { ReactComponent as EditIcon } from "../../assets/images/edit-hover.svg";
import { ReactComponent as DelIcon } from "../../assets/images/delete.svg";
import { formatDate } from "../create-protocol/utils";
import { useNewTrialState } from "../new-trail/hooks/NewTrialStateProvider";
import { useTrialsRouter } from "../../shared/custom-hooks/useTrialsRouter";
import { useTrackingEvent } from "../../shared/custom-hooks/useTracking";

const RECOGNIZED_TRIAL_STATUSES = ["ready", "activated", "draft"];

export const normalizeExecutionStatus = (record: {
  execution_status?: string;
}): string => (record?.execution_status ?? "").trim().toLowerCase();

export const TrialListColumns = (
  handleDeleteTrialData: any,
  openModal: any,
  openDrawer: any,
  toggleCompletion: any,
  handleShareReport: any,
  farmName?: string
): any => {
  const { t } = useTranslation();
  const { router, farmId } = useTrialsRouter();
  const trackTrialEvents = useTrackingEvent({
    page: "Trials Page",
    group: "Trials",
    module: "Trials",
  });

  useNewTrialState();

  const handleNavigation = (id: string, isCompleted?: boolean): void => {
    router.goToTrialsEditPage(undefined, id, false, isCompleted === true);
  };

  const trackActionClick = (
    trialActionType: "Share" | "Export" | "Edit" | "Delete",
    record: TrialDataInterface
  ): void => {
    void trackTrialEvents({
      event: TrialCreationAmplitudeEvent.TRIAL_ACTION_CLICK,
      trial_action_type: trialActionType,
      trial_id: record.id,
      protocol_id: record.protocol_id,
      field_id: record.field_ids?.[0],
    });
  };

  const { response: farmProperties } = useAxios(
    `/properties/${farmId as string}/fields?attributes=geometry`,
    true
  );

  const fieldData = Array.isArray(farmProperties?.content)
    ? farmProperties?.content
    : [];

  const getFieldName = (fieldId: string): string => {
    return fieldData?.length > 0
      ? fieldData?.find((item: any) => item?.id?.includes(fieldId))?.name
      : "";
  };

  const getSortableFieldName = (record: TrialDataInterface): string =>
    record.field_ids?.length ? getFieldName(record.field_ids[0]) ?? "" : "";

  const TRIAL_COLUMNS = [
    {
      title: formatTranslation(t("trials.tableTitle1")),
      dataIndex: "name",
      key: "name",
      sorter: (a: TrialDataInterface, b: TrialDataInterface) =>
        (a.name ?? "").localeCompare(b.name ?? ""),
      render: (text: string) => (
        <span className="trial-name" data-testid="trial-name">
          {text}
        </span>
      ),
    },
    {
      title: formatTranslation(t("trials.tableTitle4")),
      dataIndex: "field_name",
      key: "field_name",
      sorter: (a: TrialDataInterface, b: TrialDataInterface) =>
        getSortableFieldName(a).localeCompare(getSortableFieldName(b)),
      render: (_a: any, record: any) => {
        return (
          <span className="field-farm-cell" data-testid="field-name">
            <span className="field-farm-primary">
              {record?.field_ids?.length
                ? getFieldName(record.field_ids[0])
                : ""}
            </span>
            <span className="field-farm-secondary" data-testid="farm-name">
              {farmName ?? ""}
            </span>
          </span>
        );
      },
    },
    {
      title: formatTranslation(t("createProtocol.basicInfo.protocolCrop")),
      dataIndex: "crop",
      key: "crop",
      sorter: (a: TrialDataInterface, b: TrialDataInterface) =>
        (a.crop ?? "").localeCompare(b.crop ?? ""),
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: formatTranslation(t("trials.tableTitle10")),
      dataIndex: "start_date",
      key: "start_date",
      sorter: (a: TrialDataInterface, b: TrialDataInterface) =>
        new Date(b.start_date).getTime() - new Date(a.start_date).getTime(),
      render: (text: string) => <span>{formatDate(text)}</span>,
    },
    {
      title: formatTranslation(t("trials.tableTitle11")),
      dataIndex: "end_date",
      key: "end_date",
      sorter: (a: TrialDataInterface, b: TrialDataInterface) =>
        new Date(b.end_date).getTime() - new Date(a.end_date).getTime(),
      render: (text: string) => <span>{formatDate(text)}</span>,
    },
    {
      title: formatTranslation(t("trials.tableTitle2")),
      dataIndex: "protocol_name",
      key: "protocol_name",
      sorter: (a: TrialDataInterface, b: TrialDataInterface) =>
        (a.protocol_name ?? "").localeCompare(b.protocol_name ?? ""),
      render: (_: any, record: any) => (
        <span
          className="protocol-name"
          data-id={record?.id}
          onClick={(e) => {
            e.stopPropagation();
            record?.id && openDrawer(record?.protocol_id);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              if (e.key === " ") {
                e.preventDefault();
              }

              e.stopPropagation();
              record?.id && openDrawer(record?.protocol_id);
            }
          }}
          tabIndex={0}
          role="button"
        >
          {record?.protocol_name}
        </span>
      ),
    },
    {
      title: formatTranslation(t("trials.tableTitle8")),
      key: "trial_status",
      render: (_: any, record: TrialDataInterface) => {
        const status: string = record?.execution_status ?? "";
        const normalizedStatus = normalizeExecutionStatus(record);
        const statusModifier = RECOGNIZED_TRIAL_STATUSES.includes(
          normalizedStatus
        )
          ? normalizedStatus
          : "unknown";

        return (
          <span
            className={`trial-status-column trial-status-${statusModifier}`}
            data-testid="trial-status"
          >
            {status.toUpperCase()}
          </span>
        );
      },
    },
    {
      title: (
        <span className="column-title-with-info">
          {formatTranslation(t("trials.tableTitle9"))}
          <Tooltip title={formatTranslation(t("trials.activationTooltip"))}>
            <InfoCircleOutlined
              className="info-icon"
              data-testid="activation-info-icon"
            />
          </Tooltip>
        </span>
      ),
      key: "completion",
      onHeaderCell: () => ({ className: "activation-header-cell" }),
      render: (_: any, record: any) => (
        <span
          onKeyDown={(e) => {
            e.stopPropagation();
          }}
        >
          <Switch
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
            onChange={(checked: boolean) =>
              toggleCompletion(checked, record?.id)
            }
            onClick={(_checked, e) => {
              e.stopPropagation();
            }}
            checked={normalizeExecutionStatus(record) === "activated"}
          />
        </span>
      ),
    },
    {
      title: formatTranslation(t("trials.tableTitle7")),
      key: "action",
      render: (_: any, record: any) => {
        const menuItems: MenuProps["items"] = [
          {
            key: "share",
            label: (
              <Button
                data-testid="export-btn"
                className="share-report-icon"
                disabled={record?.is_exportable === false}
                onClick={() => {
                  trackActionClick("Share", record);
                  void trackTrialEvents({
                    event: TrialReportingAmplitudeEvent.TRIAL_REPORT_GENERATE,
                    trial_id: record.id,
                  });
                  handleShareReport(record);
                }}
              >
                <FileTextOutlined className="share-icon" />{" "}
                {formatTranslation(t("trials.shareReport"))}
              </Button>
            ),
          },
          {
            key: "export",
            label: (
              <Button
                data-testid="export-btn"
                className={record?.is_exportable ? "" : "export-workorder-icon"}
                disabled={record?.is_exportable === false}
                onClick={(): void => {
                  openModal(record?.id, record?.field_ids[0], record);
                }}
              >
                <DownloadIcon /> {formatTranslation(t("trials.export"))}
              </Button>
            ),
          },
          {
            key: "edit",
            label: (
              <Button
                data-testid="edit-btn"
                onClick={() => {
                  trackActionClick("Edit", record);
                  handleNavigation(record?.id, record?.completion);
                }}
              >
                <EditIcon width={18} height={18} />{" "}
                {formatTranslation(t("trials.editAction"))}
              </Button>
            ),
          },
          {
            key: "delete",
            label: (
              <Popconfirm
                rootClassName="trial-delete-popup"
                title={`${formatTranslation(
                  t("trials.deleteConfirm.prefix")
                )} ${record.name as string}?`}
                description={
                  <span className="del-label">
                    {formatTranslation(t("trials.deleteText"))}
                  </span>
                }
                icon={<QuestionCircleOutlined />}
                onConfirm={() => {
                  trackActionClick("Delete", record);
                  return handleDeleteTrialData(record?.id);
                }}
                onCancel={() => {}}
              >
                <Button className="del-btn" data-testid="delete-btn">
                  <DelIcon width={18} /> {formatTranslation(t("trials.delete"))}
                </Button>
              </Popconfirm>
            ),
          },
        ];

        return (
          <span
            onClick={(e) => {
              e.stopPropagation();
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Dropdown
              menu={{
                items: menuItems,
                className: "action-menu",
              }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <Button className="action-btn" data-testid="menu-icon">
                <span>
                  <MenuIcon className="menu-icon" />
                </span>
              </Button>
            </Dropdown>
          </span>
        );
      },
    },
  ];

  return TRIAL_COLUMNS;
};
